import React, { useState, useEffect } from "react";
import io from "socket.io-client";
import { serviceConfig } from "configs";
import { Link } from "react-router-dom";
import { routeConstants } from "navigation";
import { Htag } from "common/Htag/Htag";
import { ReactComponent as Logo } from "common/assets/icons/logo.svg";
import { P } from "common/P/P";
import { ReactComponent as LangIcon } from "common/assets/flags/tk.svg";
import { ReactComponent as ThemeIcon } from "common/assets/icons/moon-stars.svg";
import { ReactComponent as NotificationIcon } from "common/assets/icons/bell.svg";
import { ReactComponent as AvatarIcon } from "common/assets/icons/user.svg";
import { ReactComponent as PlusIcon } from "common/assets/icons/plus.svg";
import ChatContactItem from "common/ChatContactItem/ChatContactItem";
import { Button } from "common/Button/Button";
import Modal from "common/Modal/Modal";
import { Select } from "common/Select/Select";
import axios from "axios";
import { SelectItem } from "common/Select/SelectItem";

const socket = io(`${serviceConfig.websocket_port}`, {
	reconnectionDelay: 2000, // Reconnection delay in milliseconds (2 seconds)
	reconnectionDelayMax: 3000, // Maximum reconnection delay in milliseconds (3 seconds)
	reconnectionAttempts: Infinity, // Number of reconnection attempts (Infinity for unlimited)
});

const ChatPage = () => {
	const [message, setMessage] = useState<string>("");
	const [chat, setChat] = useState<any[]>([]); // Update to any[] if you're expecting objects
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [users, setUsers] = useState<any>([]);
	const [contacts, setContacts] = useState<any>([]);
	const [selectedContact, setSelectedContact] = useState("");
	const [selectedContactName, setSelectedContactName] = useState("");
	const [activeContactId, setActiveContactId] = useState("");
	const [activeContact, setActiveContact] = useState<any>(null);

	const userString = localStorage.getItem("user");
	const user = userString ? JSON.parse(userString) : null;

	const userId = user ? user?.user?._id : null; // Replace with actual user ID
	const userTypeId = user ? user?.user.userTypeId?._id : null;
	const numericUserTypeId = parseInt(userTypeId);

	const openModal = () => setIsModalOpen(true);
	const closeModal = () => setIsModalOpen(false);

	const senderId = "65a3972cafcaa4945639d988";
	const receiverId = "65a3972cafcaa4945639d98c";

	const sendMessage = async (receiverId: any) => {
		if (!message.trim()) return; // Prevent sending empty messages

		try {
			const data = {
				senderId: userId,
				receiverId: receiverId,
				message: message,
				// Add 'file' here if you implement file sending
			};

			// Emit the message to the server using socket.io
			socket.emit("sendMessage", data);

			// Reset the input field after sending the message
			setMessage("");
		} catch (error) {
			console.error("Error sending message:", error);
		}
	};

	useEffect(() => {
		socket.on("reconnect_attempt", () => {
			console.log("Attempting to reconnect...");
		});

		socket.on("reconnect", (attemptNumber) => {
			console.log(`Reconnected after ${attemptNumber} attempts`);
		});

		socket.on("reconnect_error", (error) => {
			console.error("Reconnection error:", error);
		});

		// Clean up
		return () => {
			socket.off("reconnect_attempt");
			socket.off("reconnect");
			socket.off("reconnect_error");
		};
	}, []);

	useEffect(() => {
		const receiveMessageListener = (msg: any) => {
			setChat((prevChat) => [...prevChat, msg]);
		};

		socket.on("receiveMessage", receiveMessageListener);

		return () => {
			socket.off("receiveMessage", receiveMessageListener);
		};
	}, []);

	const groupMessages = (messages: any) => {
		const grouped = [];
		let currentGroup: any = [];

		messages.forEach((message: any, index: any) => {
			// Add first message to the group
			if (index === 0) {
				currentGroup.push(message);
				return;
			}

			const prevMessage = messages[index - 1];
			const sameMinute =
				new Date(message.createdAt).getMinutes() ===
				new Date(prevMessage.createdAt).getMinutes();
			const sameSender = message.senderId === prevMessage.senderId;

			if (sameMinute && sameSender) {
				currentGroup.push(message);
			} else {
				grouped.push(currentGroup);
				currentGroup = [message];
			}
		});

		// Push the last group
		if (currentGroup.length > 0) {
			grouped.push(currentGroup);
		}

		return grouped;
	};

	const groupedMessages = groupMessages(chat);

	useEffect(() => {
		fetchContacts();
	}, []);

	const fetchUsers = async () => {
		try {
			const response = await axios.get(`${serviceConfig.apiUrl}/users`);
			setUsers(response.data.data);
		} catch (error) {
			console.error("Error fetching users:", error);
		}
	};

	const fetchContacts = async () => {
		try {
			const response = await axios.get(
				`${serviceConfig.apiUrl}/users/${userId}/contacts`
			);
			setContacts(response.data);
		} catch (error) {
			console.error("Error fetching contacts:", error);
		}
	};

	const handleSelectContact = (contactId: string, contactName: string) => {
		setSelectedContact(contactId);
		setSelectedContactName(contactName);
	};

	const handleSubmit = async () => {
		try {
			await axios.post(`${serviceConfig.apiUrl}/users/contacts`, {
				userId,
				contactId: selectedContact,
			});
			const newContact = users.find(
				(user: any) => user._id === selectedContact
			);
			if (newContact) {
				setContacts([...contacts, newContact]);
			}
			closeModal();
		} catch (error) {
			console.error("Error adding contact:", error);
		}
	};

	const fetchChats = async (contactId: string) => {
		try {
			const response = await axios.post(
				`${serviceConfig.apiUrl}/chat/messages`,
				{
					senderId: userId,
					receiverId: contactId,
				}
			);
			setChat(response.data);
		} catch (error) {
			console.error("Error fetching chats:", error);
		}
	};

	useEffect(() => {
		if (isModalOpen) {
			fetchUsers(); // Fetch all users when the modal opens
		}
	}, [isModalOpen]);

	useEffect(() => {
		if (activeContactId) {
			fetchChats(activeContactId);
		}
	}, [activeContactId]);

	const handleContactClick = (contact: any) => {
		setActiveContact(contact);
		setActiveContactId(contact._id);
		fetchChats(contact._id); // Fetch chat messages with the selected contact
	};

	return (
		<div className="grid grid-cols-1">
			{/* navbar */}
			<div className="grid px-6 py-0 border-b grid-cols-maxTwo place-content-between place-items-center border-strokeColor">
				<Link to={routeConstants.root.route}>
					<div className="grid gap-2 grid-cols-maxTwo place-items-center">
						<Logo className="w-8 h-8" />
						{/* <img src={logoPicture} alt="logo" className="w-10 h-10" /> */}
						<Htag
							tag="h2"
							type="semibold"
							className="text-3xl cursor-pointer text-mainColor"
						>
							DigiDocs Chat
						</Htag>
					</div>
				</Link>
				<div className="grid grid-cols-1 gap-4 px-6 py-3 place-items-end">
					{/* <LangIcon />
					<ThemeIcon />
					<div className="relative">
						<NotificationIcon />
						<div className="absolute top-[-5px] right-[-5px] grid px-[0.4rem] py-[0.1rem] rounded-full place-items-center place-content-center bg-redColor">
							<P size="t2" type="semibold" className="text-white">
								4
							</P>
						</div>
					</div> */}
					<div className="grid gap-3 grid-cols-maxTwo place-items-center">
						<div className="p-1 rounded-full bg-grayOne">
							<AvatarIcon />
						</div>
						<div className="grid">
							<P size="t1" type="medium">
								{user ? `${user.user.name.charAt(0)}.${user.user.sirname}` : ""}
							</P>
							<P size="t2" type="regular" className="text-grayTwo">
								{user ? user.user.userTypeId?.positionName : ""}
							</P>
						</div>
					</div>
				</div>
			</div>

			<div className="grid grid-cols-maxFr h-[94vh]">
				{/* Sidebar container with flexbox */}
				<div className="flex flex-col py-[10px] px-3 w-[360px] border-r border-strokeColor ">
					{/* Title Section */}
					<P size="p1" type="medium" className="text-mainColor px-4 py-[10px]">
						Çatlar
					</P>

					{/* Contacts Section */}
					<div className="flex-grow overflow-y-auto">
						{/* Contacts */}
						<div className="grid grid-flow-row grid-cols-1 gap-1 auto-cols-max">
							{contacts.map((contact: any) => (
								<ChatContactItem
									key={contact._id}
									name={`${contact.name} ${contact.sirname}`}
									positionName={contact.userTypeId.positionName}
									active={activeContactId === contact._id}
									onClick={() => handleContactClick(contact)}
								/>
							))}

							{/* Add more contacts here */}
						</div>
					</div>

					{/* Fixed Button at the Bottom */}
					<Button
						appearance="main"
						icon={<PlusIcon className="stroke-white" />}
						width="full"
						className="mt-3" // This pushes the button to the bottom
						onClick={openModal}
					>
						Kontakt goşmak
					</Button>
				</div>

				{/* chat div */}
				{activeContact && (
					<div>
						<div className="grid grid-cols-1 px-4 py-2 border-b border-strokeColor">
							<ChatContactItem
								name={`${activeContact.name} ${activeContact.sirname}`}
								positionName={activeContact.userTypeId.positionName}
								cursor={false}
							/>
						</div>

						<div className="p-5 bg-[#f0f2fd] flex flex-col h-[92%]">
							{/* Chat Content */}
							<div className="flex-1 overflow-y-auto">
								{groupedMessages.map((group, groupIndex) => (
									<div key={groupIndex}>
										{group.map((msg: any, index: any) => (
											<div
												key={msg._id}
												className={`max-w-xs w-max mb-4 ${
													msg.senderId === userId ? "ml-auto mr-4" : "ml-4"
												}`}
											>
												<div
													className={`px-4 py-[10px] shadow-shadow ${
														msg.senderId === userId
															? "bg-mainColor rounded-b-md rounded-tl-md"
															: "bg-white rounded-b-md rounded-tr-md"
													}`}
												>
													<P
														size="p3"
														type="regular"
														className={`${
															msg.senderId === userId
																? "text-white"
																: "text-textColor"
														}`}
													>
														{msg.message}
													</P>
												</div>
												{/* Show time below the last message in the group */}
												{index === group.length - 1 && (
													<div className="mt-2 text-xs text-gray-500">
														{new Date(msg.createdAt).toLocaleTimeString([], {
															hour: "2-digit",
															minute: "2-digit",
														})}
													</div>
												)}
											</div>
										))}
									</div>
								))}
							</div>

							{/* Message Input */}
							<div className="flex items-center px-5 py-2 bg-white rounded-md shadow-md">
								<input
									type="text"
									className="flex-1 p-2 mr-4 border-none rounded-md"
									placeholder="Hatyňyzy ýazyň"
									value={message}
									onChange={(e) => setMessage(e.target.value)}
									onKeyDown={(e) => {
										if (e.key === "Enter") {
											sendMessage(activeContactId);
										}
									}}
								/>
								<Button
									appearance="main"
									onClick={() => sendMessage(activeContactId)}
								>
									Iber
								</Button>
							</div>
						</div>
					</div>
				)}
				<Modal
					isOpen={isModalOpen}
					onClose={closeModal}
					title="Kontakt goşmak"
					width="w-[600px]"
					scroll={false}
				>
					<div className="grid grid-flow-row grid-cols-1 gap-6 auto-rows-max">
						<div className="grid grid-flow-row grid-cols-1 gap-4 auto-rows-max">
							<Select
								label="Kontaktyň ady"
								onSelect={(contactId) => {
									const selectedUser = users.find(
										(user: any) => user._id === contactId
									);
									handleSelectContact(
										contactId,
										`${selectedUser.name} ${selectedUser.sirname}`
									);
								}}
								value={selectedContactName}
								defaultSelectLabel=""
							>
								{users.map((user: any) => (
									<SelectItem key={user._id} value={user._id}>
										{user.name.charAt(0)}. {user.sirname}
									</SelectItem>
								))}
							</Select>

							<Button
								appearance="main"
								className="place-self-end"
								paddingX="px-5"
								paddingY="py-[10px]"
								onClick={handleSubmit}
							>
								Goşmak
							</Button>
						</div>
					</div>
				</Modal>
			</div>
		</div>
	);
};

export default ChatPage;
